import { useState } from 'react';

const profiles = [
  {
    icon: 'ri-line-chart-line',
    title: 'Performance Feels Unstable',
    description:
      'Not just your face — your focus, steadiness, and vitality feel inconsistent. You want to restore control and presence.',
    stat: '84%',
    statLabel: 'link facial instability to overall performance decline',
  },
  {
    icon: 'ri-eye-close-line',
    // Use double quotes to avoid breaking the string with an apostrophe
    title: "You Look Tired — But You're Not",
    description:
      "Your face tells a story of exhaustion that doesn't match your internal drive.",
    stat: '78%',
    statLabel: 'of clients report this as their first concern',
  },
  {
    icon: 'ri-contrast-drop-2-line',
    title: 'Your Face Has Lost Its Sharpness',
    // Use double quotes for the apostrophe in "don't"
    description:
      "Subtle changes in definition and tone that don't match your capability. Your face feels dull, less precise than it used to be.",
    stat: '65%',
    statLabel: 'notice changes after prolonged high-pressure periods',
  },
  {
    icon: 'ri-skull-2-line',
    title: 'Jaw Tension Is Constant',
    description:
      'You clench without realizing. TMJ discomfort. Facial tightness that never fully releases, even when you try to relax.',
    stat: '3x',
    statLabel: 'more common in high-functioning professionals',
  },
  {
    icon: 'ri-pulse-line',
    title: 'Stress Shows on Your Face',
    description:
      'High-pressure weeks leave visible marks — facial asymmetry, premature lines, skin sensitivity. Something feels off.',
    stat: '91%',
    statLabel: 'experience visible facial changes under chronic stress',
  },
  {
    icon: 'ri-shield-cross-line',
    title: 'You Refuse Botox & Fillers',
    description:
      'You want natural solutions with structural logic. You\'re open to acupuncture and nervous system regulation.',
    stat: '2x',
    statLabel: 'growth in natural alternative seekers since 2022',
  },
];

const IdealClient = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  return (
    <section id="client-profile" className="py-8 sm:py-20 px-6 lg:px-12 bg-warmWhite relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-bronze/20 to-transparent"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-sand/30 rounded-full blur-[100px] opacity-50 translate-y-1/3 -translate-x-1/4"></div>

      <div className="relative max-w-7xl mx-auto">

        {/* Header */}
        <div className="mb-5 sm:mb-16">
          <div className="flex items-center gap-3 mb-3 sm:mb-4">
            <div className="w-8 h-[1px] bg-bronze"></div>
            <span className="text-bronze text-[10px] font-sans font-semibold tracking-[0.3em] uppercase">
              Who This Is For
            </span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-end">
            <h2 className="font-display text-2xl sm:text-4xl md:text-5xl font-light text-charcoal italic leading-tight">
              Designed for High-Functioning<br className="hidden sm:block" /> Women Who Recognize This
            </h2>
            <p className="hidden sm:block text-textGray text-[13px] font-sans font-light leading-relaxed lg:text-right lg:max-w-sm">
              For women who invest in consistency, honor holistic balance, and seek beauty that endures.
            </p>
          </div>
        </div>

        {/* Mobile: compact 2-col icon list */}
        <div className="sm:hidden grid grid-cols-2 gap-x-5 gap-y-2">
          {profiles.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-bronze text-[13px]`}></i>
              </div>
              <span
                className="text-charcoal/75 text-[11px] font-sans font-light leading-snug"
                dangerouslySetInnerHTML={{ __html: item.title }}
              />
            </div>
          ))}
        </div>

        {/* Desktop: original two-column layout */}
        <div className="hidden sm:flex flex-col lg:flex-row gap-8 items-stretch">

          {/* Left: Image */}
          <div className="lg:w-[38%] flex-shrink-0">
            <div className="relative w-full h-[520px] rounded-2xl overflow-hidden group">
              <img
                src="https://static.readdy.ai/image/296ab9838c8a2f1e2b2bac03a156c5b2/33576684eb6d7b49b706b5376192e383.png"
                alt="High-functioning professional woman - ideal client portrait"
                title="Stress-driven facial changes in high-functioning women"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-charcoal/10 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-7">
                <p className="font-display text-white text-lg italic font-light leading-relaxed mb-2">
                  &ldquo;I don&rsquo;t look like myself anymore.&rdquo;
                </p>
                <p className="text-white/60 text-[10px] font-sans tracking-widest uppercase">
                  The most common thing we hear
                </p>
              </div>
              <div className="absolute top-4 left-4 w-12 h-12 border-t border-l border-white/30"></div>
              <div className="absolute bottom-4 right-4 w-12 h-12 border-b border-r border-white/30"></div>
            </div>
          </div>

          {/* Right: Grid Cards */}
          <div className="lg:w-[62%]">
            <div className="grid grid-cols-2 gap-4">
              {profiles.map((item, index) => (
                <div
                  key={index}
                  className={`group relative bg-white rounded-2xl p-6 border transition-all duration-500 cursor-pointer ${
                    activeIndex === index
                      ? 'border-bronze/30 shadow-elevated -translate-y-1'
                      : 'border-warmGray/10 hover:border-warmGray/25 hover:shadow-soft hover:-translate-y-0.5'
                  }`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors duration-300 ${activeIndex === index ? 'bg-bronze/10' : 'bg-cream'}`}>
                      <i className={`${item.icon} text-base transition-colors duration-300 ${activeIndex === index ? 'text-bronze' : 'text-charcoal/50'}`}></i>
                    </div>
                    <span className={`text-lg font-display font-light transition-colors duration-300 ${activeIndex === index ? 'text-bronze' : 'text-charcoal/20'}`}>
                      {item.stat}
                    </span>
                  </div>
                  <h3 className="font-display text-[0.95rem] font-medium text-charcoal mb-2 italic leading-snug" dangerouslySetInnerHTML={{ __html: item.title }} />
                  <p className="text-textGray text-[11px] font-sans font-light leading-relaxed mb-3">{item.description}</p>
                  <div className={`pt-3 border-t transition-colors duration-300 ${activeIndex === index ? 'border-bronze/15' : 'border-warmGray/10'}`}>
                    <p className="text-[9px] font-sans text-textGray/70 tracking-wide uppercase leading-relaxed">{item.statLabel}</p>
                  </div>
                  <div className={`absolute top-2 right-2 w-1.5 h-1.5 rounded-full transition-all duration-300 ${activeIndex === index ? 'bg-bronze scale-100' : 'bg-transparent scale-0'}`}></div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* Bottom CTA strip */}
        <div className="mt-4 sm:mt-12 flex flex-col sm:flex-row items-center justify-center gap-6 px-4 sm:px-10 py-4 sm:py-8 bg-white/50 border border-warmGray/20 rounded-2xl">
          <p className="text-charcoal text-[12px] sm:text-[13px] font-display italic font-light text-center sm:text-left">
            Stress-driven facial instability is an early signal of performance destabilization.
          </p>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-bronze/20 to-transparent"></div>
    </section>
  );
};

export default IdealClient;
